/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import LeaveSummaryCards from "./components/leavesummaryCard/leaveSummaryCard";
import LeaveList from "./components/leaveTable/leaveTable";
import { Button, Group, Modal, Select, Text, Title } from "@mantine/core";
import { Download, UserPlus, X } from "tabler-icons-react";
import AddLeaveRequestModal from "./components/addLeave";
import { PermissionButton } from "api/permission/buttonpermission";
import { useUser } from "hooks/useUser";
import { useExportLeaveSummaryPDF } from "api/newLeave/leaveSummary";
import { useGetScopedUsersList } from "api/users/getScopedUsersList";

export default function LeaveEmployees() {
  const { user } = useUser();
  const [searchEmail, setSearchEmail] = useState<string>("");
  const [inputSearchEmail, setInputSearchEmail] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<{
    firstName: string;
    lastName: string;
  } | null>(null);
  const { data: scopedUsersData, isLoading: scopedUsersLoading } =
    useGetScopedUsersList(1, 100, searchEmail || undefined);

  const isManager = user?.role != "member";

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const isSelectingUserRef = useRef(false);

  const [isAddLeaveOpen, setIsAddLeaveOpen] = useState(false);
  const [isConfirmDownloadOpen, setIsConfirmDownloadOpen] = useState(false);

  const { mutate: exportLeaveSummaryPDF, isLoading: isExporting } =
    useExportLeaveSummaryPDF();

  useEffect(() => {
    if (selectedUserId && scopedUsersData?.data) {
      const user = scopedUsersData.data.find((u) => u._id === selectedUserId);
      if (user) {
        setSelectedUser({
          firstName: user.firstName,
          lastName: user.lastName,
        });
      }
    } else if (!selectedUserId) {
      setSelectedUser(null);
    }
  }, [selectedUserId, scopedUsersData?.data]);

  const usersDropdownData = useMemo(() => {
    if (!scopedUsersData?.data) return [];
    return scopedUsersData.data.map((u) => ({
      value: u._id,
      label: `${u.firstName} ${u.lastName} (${u.email})`,
    }));
  }, [scopedUsersData]);

  const handleUserSelect = (userId: string | null) => {
    setSelectedUserId(userId);
    if (userId) {
      isSelectingUserRef.current = true;
      setInputSearchEmail("");
      // Reset the flag after a brief delay to allow normal search again
      setTimeout(() => {
        isSelectingUserRef.current = false;
      }, 100);
    } else {
      // Only clear search if user is deselected
      setSearchEmail("");
    }
  };

  const openConfirmDownload = () => {
    setIsConfirmDownloadOpen(true);
  };

  const closeConfirmDownload = () => {
    setIsConfirmDownloadOpen(false);
  };

  const handleConfirmDownload = () => {
    if (!user?._id || !user?.org?._id) {
      console.warn("User ID or Org ID missing");
      closeConfirmDownload();
      return;
    }

    exportLeaveSummaryPDF(
      {
        userId: user._id,
        orgId: user.org._id,
      },
      {
        onSuccess: () => {
          closeConfirmDownload();
        },
        onError: (err: any) => {
          console.error("Export failed", err);
          closeConfirmDownload();
        },
      },
    );
  };

  return (
    <div>
      <Modal
        opened={isConfirmDownloadOpen}
        onClose={closeConfirmDownload}
        centered
        size="md"
        radius="md"
        padding="xl"
        title={
          <Text fw={600} fz="lg">
            Confirm Download
          </Text>
        }
      >
        <Text size="lg" mt="sm" mb="xl" color="dimmed">
          Are you sure you want to download the Leave Summary file?
        </Text>

        <Group position="apart" mt="xl">
          <Button
            color="gray"
            onClick={closeConfirmDownload}
            size="md"
            disabled={isExporting}
          >
            Cancel
          </Button>

          <Button
            size="md"
            loading={isExporting}
            onClick={handleConfirmDownload}
          >
            {isExporting ? "Downloading..." : "Yes, Download"}
          </Button>
        </Group>
      </Modal>

      <Group position="apart" mb="md">
        <Title order={2}>
          Personal Leave{" "}
          {selectedUser &&
            `- ${selectedUser.firstName} ${selectedUser.lastName}`}
        </Title>
        <Group>
          {isManager && (
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <Select
                data={usersDropdownData}
                value={selectedUserId}
                onChange={handleUserSelect}
                onSearchChange={(val) => {
                  setInputSearchEmail(val);
                }}
                placeholder="Select user"
                searchable
                clearable
                disabled={scopedUsersLoading}
                sx={{ width: 250 }}
              />
              {selectedUserId && (
                <Button
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => {
                    setSelectedUserId(null);
                    setSearchEmail("");
                  }}
                  title="Show my attendance"
                >
                  <X size={16} />
                </Button>
              )}
            </div>
          )}
          {!selectedUser && (
            <>
              <PermissionButton
                permission="employees.balanceSummaryDownload"
                leftIcon={<Download size={16} />}
                loading={isExporting}
                onClick={openConfirmDownload}
              >
                {isExporting ? "Downloading..." : "Leave Summary Download"}
              </PermissionButton>

              <PermissionButton
                permission="employees.addleave"
                leftIcon={<UserPlus size={16} />}
                onClick={() => setIsAddLeaveOpen(true)}
              >
                Add Leave
              </PermissionButton>
            </>
          )}
        </Group>
      </Group>

      <div style={{ marginBottom: "15px" }}>
        <LeaveSummaryCards userId={selectedUserId || undefined} />
      </div>
      <div style={{ width: "100%" }}>
        <LeaveList userId={selectedUserId || undefined} />
      </div>

      <AddLeaveRequestModal
        opened={isAddLeaveOpen}
        onClose={() => setIsAddLeaveOpen(false)}
      />
    </div>
  );
}
